import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import styled from "styled-components";
import { Col, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import StyledText from "../../StyledComponents/StyledText";
import { Tabs } from "../../Tabs/Tabs";
import FieldPreview from "../FieldPreviews/FieldPreview";
import FieldSettingsPanel from "../FieldSettings/FieldSettingsPanel";
import { Button } from "../../form/Button";
import FormPreviewModal from "../FormPreview/FormPreviewModal";
import DeletePrompt from "../../DeletePrompt";
import { SkeletonCell } from "../../SkeletonCell";
import StyledToolTip from "../../StyledComponents/StyledTooltip";
import SearchBar from "../../searchBar/SearchBar";
import { FieldChip } from "./FieldChip";
import { StyledHr } from "../../../features/organizations/OrganizationOverview/OrganizationOverview";

// import SettingsIcon from "../../../assets/icons/settingsIcon.svg";
import PageButtonsBG from "../../../assets/icons/pageButtons.svg";
import FormIcon from "../../../assets/icons/FormIcon";
import FormsIcon from "../../../assets/icons/FormsIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CrossIcon from "../../../assets/icons/CrossIcon";
import FormDraftIcon from "../../../assets/icons/FormDraftIcon";
import FormTempalteIcon from "../../../assets/FormDraftIcon";
import AddWhiteIcon from "../../../assets/icons/AddWhiteIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import DraggableIcon from "../../../assets/icons/DraggableIcon";
import RightArrow from "../../../assets/icons/RightArrow";
import FieldChipHeadingIcon from "../../../assets/icons/FieldChipHeadingIcon";
import FieldChipParagraphIcon from "../../../assets/icons/FieldChipParagraphIcon";
import FieldChipSeparatorIcon from "../../../assets/icons/FieldChipSeparatorIcon";

import {
  Field,
  FieldConfig,
  FieldDataTypeEnum,
  FormRow,
  FORM_BUILDER_TABS,
  DeleteItemType,
  Component,
  FormFieldTest,
  FormPage,
  Form,
} from "../../../types/types";

import {
  useGetAllComponentsQuery,
  useGetAllFieldsAllPagesQuery,
  useGetAllFieldsQuery,
  useGetCustomFormByIdQuery,
  usePublishCustomFormMutation,
  useUpdateCustomFormMutation
} from "../../../api/formsApiSlice";

import { useToast } from "../../toast/ToastContext";
import { CreateFormTheme } from "../../../utils/themes";
import { formatLabelWithUnderscore } from "../../../utils/helper";
import { EditableTitle } from "../EditableTitle";
import CreateFormModal from "./CreateFormModal";
import { useGetScopesforFieldsQuery } from "../../../api/formsApiSlice";
import { IconButton } from "../FieldPreviews/FieldActions";
import { useTheme } from "../../../middleware/ThemeContext";
import SettingsIcon from "../../../assets/icons/SettingsIcon";
import CloseIcon from "../../../assets/icons/CloseIcon";
import ConfirmationPrompt from "../../StyledComponents/ConfirmationPrompt";
import SimpleBar from "simplebar-react";
import { LoadingSpinner } from "../../../features/ui/LoadingSpinner";
import { getIconForDataType } from "../FieldSettings/fieldSettingsHelper";
import ComponentIconDT from "../../../assets/icons/ComponentIconDT";
import NoData from "../../listing/NoData";
import { set } from "lodash";
import LeftArrow from "../../../assets/icons/LeftArrow";

const INITIAL_PAGE: FormPage = {
  id: 1,
  title: "Page 1",
  description: "",
  formRows: []
};

const FormBuilder: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();
  const { id: urlFormId } = useParams<{ id: string }>();

  const formId = urlFormId ? Number(urlFormId) : null;
  const [form, setForm] = useState<Form | null>(null);
  const [fields, setFields] = useState<Field[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [formPages, setPages] = useState<FormPage[]>([INITIAL_PAGE]);
  const [activeTab, setActiveTab] = useState<string>("System Fields");
  const [draggedFieldKey, setDraggedFieldKey] = useState<string | null>(null);
  const [selectedPageIndex, setSelectedPageIndex] = useState<number>(0);
  const [showPreview, setShowPreview] = useState(false);
  const [savingCount, setSavingCount] = useState(0);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DeleteItemType | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingRow, setIsDraggingRow] = useState(false);
  // Row drag specific state (distinct from field/component drag)
  const [dragMode, setDragMode] = useState<'row' | 'field' | 'component' | null>(null);
  const [rowDragIndex, setRowDragIndex] = useState<number | null>(null);
  const [rowInsertionIndex, setRowInsertionIndex] = useState<number | null>(null);
  const [rowDragPageIndex, setRowDragPageIndex] = useState<number | null>(null);
  const [rowDragHasMoved, setRowDragHasMoved] = useState(false);

  // Finalize row drop (vertical reordering) - kept at component scope so drop zones can invoke it
  const finalizeRowDrop = useCallback((pageIndex: number, insertionIdx: number) => {
    if (rowDragIndex === null || rowDragPageIndex === null) return;
    // Only allow intra-page moves for now
    if (pageIndex !== rowDragPageIndex) return;
    if (insertionIdx === null) return;
    if (rowDragIndex === insertionIdx || rowDragIndex + 1 === insertionIdx) {
      // If dropping directly back to same spot (before or after its own position with no net move) skip
      // For clarity: insertionIdx refers to position between rows; moving a row immediately after itself yields no change.
      return;
    }
    setPages(prev => {
      const newPages = [...prev];
      const page = { ...newPages[pageIndex] };
      const rows = [...page.formRows];
      const [dragged] = rows.splice(rowDragIndex, 1);
      // Adjust insertion index when original row removed above target boundary
      const adjusted = rowDragIndex < insertionIdx ? insertionIdx - 1 : insertionIdx;
      rows.splice(adjusted, 0, dragged);
      page.formRows = rows;
      newPages[pageIndex] = page;
      return newPages;
    });
  }, [rowDragIndex, rowDragPageIndex, setPages]);
  const [formTitle, setFormTitle] = useState("Untitled Form");
  const [searchTerm, setSearchTerm] = useState("");
  const [leftColumnPage, setPage] = useState(1);
  const [leftColumnLimit] = useState(20);
  const [selectedRow, setSelectedRow] = useState<{ pageIndex: number, rowIndex: number } | null>(null);
  const [showConfirmationPrompt, setShowConfirmationPrompt] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState<number | null>(null);
  // Which inline insertion dropzone is hovered while dragging
  const [hoveredDropZone, setHoveredDropZone] = useState<{ pageIndex: number; dropIndex: number } | null>(null);
  // Visual hint: when dragging a field over a SINGLE field row
  const [hoverSingleRow, setHoverSingleRow] = useState<{ pageIndex: number; rowIndex: number } | null>(null);

  const formAreaRef = useRef<HTMLDivElement>(null);
  const dropZoneRefs = useRef<(HTMLDivElement | null)[]>([]);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Auto-save-on-leave refs (avoid repeated saves due to changing dependencies)
  const initialPathRef = useRef<string>(location.pathname);
  const formIdRef = useRef<number | null>(formId);
  const hasHydratedRef = useRef(false);
  const skipDirtyOnceRef = useRef(false);
  const isDirtyRef = useRef(false);
  const autoSaveTriggeredRef = useRef(false);
  const prepareFormDataRef = useRef<null | (() => any)>(null);
  const updateCustomFormRef = useRef<any>(null);
  const [scrollHeight, setScrollHeight] = useState("40vh");

  useEffect(() => {
    const updateHeight = () => {
      if (scrollContainerRef.current) {
        const rect = scrollContainerRef.current.getBoundingClientRect();
        const availableHeight = window.innerHeight - rect.top - 20; // 20px padding from bottom
        setScrollHeight(`${availableHeight}px`);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  // API Queries
  const {
    data: formsData,
    isLoading: isLoadingForm,
    isError: isFormError,
    refetch: refetchForm,
  } = useGetCustomFormByIdQuery(formId, {
    skip: !formId,
    refetchOnMountOrArgChange: true
  });
  const [showEditFormModal, setShowEditFormModal] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [fieldSettingsTarget, setFieldSettingsTarget] = useState<{
    pageIndex: number;
    rowIndex: number;
    fieldIndex: number;
    field: FormFieldTest;
  } | null>(null);
  const [fieldSettingsOpen, setFieldSettingsOpen] = useState(false);

  // Fetch scopes to resolve scope display name
  const { data: scopesData } = useGetScopesforFieldsQuery({ isVisible: true });
  const scopeLabel = useMemo(() => {
    const scopeId = (formsData as any)?.scopeId;
    const arr = scopesData?.scopes || [];
    const match = arr.find((s: any) => s?.id === scopeId);
    return match?.displayName || (scopeId ? `Scope ${scopeId}` : undefined);
  }, [scopesData, formsData]);

  const { data: allFields, isLoading: isLoadingFields, isFetching: isFetchingFields, refetch: refetchFields } = useGetAllFieldsAllPagesQuery({
    query: (activeTab === "System Fields" || activeTab === "Custom Fields") ? searchTerm : "",
    scopeId: formsData?.scopeId,
    ...(activeTab === "UI Elements" ? { ui_fields: true } : {}),
  }, {
    skip: !formsData?.scopeId,
    refetchOnMountOrArgChange: true,
  });

  const { data: componentsData, refetch: refetchComponents } = useGetAllComponentsQuery({
    query: activeTab === "Components" ? searchTerm : "",
    // leftColumnPage,
    // leftColumnLimit
    sort: 'createdAt',
    sortDir: 'ASC',
    scopeId: formsData?.scopeId, // Add scopeId from the form data
  }, {
    refetchOnMountOrArgChange: true
  });

  // Ensure queries refetch when scopeId becomes available or changes.
  useEffect(() => {
    if (formsData?.scopeId) {
      try {
        refetchFields?.();
      } catch (e) {
        // swallow - refetch may be undefined in some test environments
      }
      try {
        refetchComponents?.();
      } catch (e) {
        // swallow
      }
    }
  }, [formsData?.scopeId, refetchFields, refetchComponents]);

  // Refetch when route changes (navigating back to this page) or when window regains focus
  useEffect(() => {
    try {
      refetchFields?.();
    } catch (e) { }
    try {
      refetchComponents?.();
    } catch (e) { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // useEffect(() => {
  //   const onVisibility = () => {
  //     if (!document.hidden) {
  //       try { refetchFields?.(); } catch (e) { }
  //       try { refetchComponents?.(); } catch (e) { }
  //     }
  //   };
  //   window.addEventListener('visibilitychange', onVisibility);
  //   window.addEventListener('focus', onVisibility);
  //   return () => {
  //     window.removeEventListener('visibilitychange', onVisibility);
  //     window.removeEventListener('focus', onVisibility);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [refetchFields, refetchComponents]);



  const [updateCustomForm, { isLoading: updatedCustomerLoading }] = useUpdateCustomFormMutation();
  const [publishCustomForm, { isLoading: publishLoading }] = usePublishCustomFormMutation();
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Keep latest values in refs so unmount + navigation handlers don't re-register
  useEffect(() => {
    formIdRef.current = formId;
  }, [formId]);

  useEffect(() => {
    updateCustomFormRef.current = updateCustomForm;
  }, [updateCustomForm]);

  const mergeDisplayOnlyConfigIntoField = useCallback((formField: FormFieldTest, sourceField?: Field | null): FormFieldTest => {
    const resolvedField = sourceField ?? formField.field;
    if (!resolvedField) return formField;

    if (resolvedField.dataType !== FieldDataTypeEnum.DISPLAY_ONLY) {
      return {
        ...formField,
        field: resolvedField,
      };
    }

    return {
      ...formField,
      field: {
        ...resolvedField,
        // Prefer saved per-formField config from API for display-only fields
        config: formField?.config ?? resolvedField?.config,
      },
    };
  }, []);

  // Effects
  useEffect(() => {
    if (formsData) {
      const mutableForm = JSON.parse(JSON.stringify(formsData));
      setForm(mutableForm);
      const hydratedPages = mutableForm?.formPages.length
        ? mutableForm.formPages.map((page: FormPage) => ({
            ...page,
            formRows: (page.formRows || []).map((row: FormRow) => ({
              ...row,
              formFields: (row.formFields || []).map((formField: FormFieldTest) =>
                mergeDisplayOnlyConfigIntoField(formField)
              )
            }))
          }))
        : [INITIAL_PAGE];

      setPages(hydratedPages);
      setFormTitle(mutableForm?.name || "Untitled Form");

      // hydration: don't treat this as a user edit
      hasHydratedRef.current = true;
      skipDirtyOnceRef.current = true;
      isDirtyRef.current = false;
      autoSaveTriggeredRef.current = false;
    } else if (!formId) {
      setPages([INITIAL_PAGE]);

      // new form: initial state shouldn't mark dirty until user edits
      hasHydratedRef.current = true;
      skipDirtyOnceRef.current = true;
      isDirtyRef.current = false;
      autoSaveTriggeredRef.current = false;
    }
  }, [formsData, formId, mergeDisplayOnlyConfigIntoField]);

  useEffect(() => {
    if (allFields) {
      setFields(allFields);
    }
  }, [allFields]);

  useEffect(() => {
    if (fields.length > 0 && formPages.length > 0) {
      // This effect patches field metadata onto existing rows; it shouldn't mark the form as user-edited.
      skipDirtyOnceRef.current = true;
      setPages(prevPages => prevPages.map(page => ({
        ...page,
        formRows: page.formRows.map(row => ({
          ...row,
          formFields: row.formFields.map(formField => {
            const matchedField = fields.find(f => f.id === (formField?.field?.id ?? formField.id));
            const mergedFormField = mergeDisplayOnlyConfigIntoField(formField, matchedField || formField.field);

            return {
              ...mergedFormField,
              isRequired: formField.isRequired !== undefined ? formField.isRequired : matchedField?.config?.uiMeta?.isRequired || false
            };
          })
        }))
      })));
    }
  }, [fields, formPages.length, mergeDisplayOnlyConfigIntoField]);

  // Mark dirty after initial hydration and after internal patch effects
  useEffect(() => {
    if (!hasHydratedRef.current) return;
    if (skipDirtyOnceRef.current) {
      skipDirtyOnceRef.current = false;
      return;
    }

    isDirtyRef.current = true;
  }, [formPages, formTitle]);

  useEffect(() => {
    if (componentsData?.components) {
      setComponents(leftColumnPage === 1 ? componentsData?.components : [...components, ...componentsData?.components]);
    }
  }, [componentsData, leftColumnPage]);

  // Handlers
  const handleAddPage = useCallback(() => {
    // generate a default title and ensure uniqueness
    const baseTitle = `Page`;
    let idx = formPages.length + 1;
    let candidate = `${baseTitle} ${idx}`;
    const existingTitles = new Set(formPages.map(p => (p.title || '').trim().toLowerCase()));
    while (existingTitles.has(candidate.toLowerCase())) {
      idx += 1;
      candidate = `${baseTitle} ${idx}`;
      // safety
      if (idx > 1000) break;
    }

    if (existingTitles.has(candidate.toLowerCase())) {
      showToast("Warning", "Could not create a new page: duplicate page title.", "warning");
      return;
    }

    const newPageId = formPages.length + 1;
    const newPage: FormPage = {
      id: newPageId,
      title: candidate,
      description: '',
      formRows: []
    };

    setPages(prev => [...prev, newPage]);
    setSelectedPageIndex(newPageId - 1);
  }, [formPages, showToast]);

  const handlePageUpdate = useCallback((pageId: number, updates: Partial<FormPage>) => {
    // If title is being updated, enforce uniqueness (case-insensitive)
    const newTitle = updates.title?.trim();
    if (newTitle) {
      const duplicate = formPages.some(p => p.id !== pageId && (p.title || '').trim().toLowerCase() === newTitle.toLowerCase());
      if (duplicate) {
        showToast("Warning", `Page name "${newTitle}" is already in use. Choose a different name.`, "warning");
        return;
      }
    }

    setPages(prev => prev.map(page =>
      page?.id === pageId ? { ...page, ...updates } : page
    ));
  }, [formPages, showToast]);

  const handleDeletePage = useCallback((pageIndex: number) => {
    if (formPages.length <= 1) {
      showToast("Cannot Delete", "A form must have at least one page.", "error");
      return;
    }

    setPages(prev => {
      const newPages = [...prev];
      newPages.splice(pageIndex, 1);

      // Only update IDs, preserve existing titles
      return newPages.map((page, index) => ({
        ...page,
        id: index + 1
        // Don't update the title here - keep the original title
      }));
    });

    setSelectedPageIndex(prevIndex => {
      if (pageIndex === 0) {
        // Deleted first page → select new first page (index 0)
        return 0;
      } else if (pageIndex >= formPages.length - 1) {
        // Deleted last page → move one back
        return prevIndex - 1;
      } else {
        // Deleted a middle page → select the same index (new page shifted)
        return pageIndex;
      }
    });
  }, [formPages.length, showToast]);

  const handleDrop = useCallback((
    e: React.DragEvent<HTMLDivElement>,
    pageIndex: number,
    rowIndex: number,
    alignment?: 0 | 1 | 2,
    insertNewRow?: boolean
  ) => {
    e.preventDefault();
    const idStr = e.dataTransfer.getData("id");
    if (!idStr) return;
    const droppedType = e.dataTransfer.getData("entityType") || e.dataTransfer.getData("type");
    const droppedEntityId = Number(idStr);
    const matchedComponent = components.find(c => c?.id === droppedEntityId);
    const matchedField = fields.find(f => f?.id === droppedEntityId);

    // Determine if the drop is happening at the end of the current page's rows
    const currentRowCount = formPages[pageIndex]?.formRows.length || 0;
    const droppingAtEnd = rowIndex === currentRowCount; // insertion index equals current length

    setPages(prevPages => {
      const updatedPages = JSON.parse(JSON.stringify(prevPages));
      const updatedPage = { ...updatedPages[pageIndex] };
      let updatedRows = [...updatedPage.formRows];
      const isMainDropZone = rowIndex === updatedRows.length;

      // Decide type based on explicit payload or fallback matches
      const isComponentDrop = droppedType === 'component' || (!droppedType && matchedComponent && !matchedField);
      const isFieldDrop = droppedType === 'field' || (!droppedType && matchedField);

      if (isComponentDrop && matchedComponent) {
        const timestampBase = Date.now();
        const componentFormFields: FormFieldTest[] = matchedComponent?.componentFields.map((cf: any, idx: number) => ({
          id: cf?.componentId ?? timestampBase + idx,
          field: cf?.fieldComponent ? { ...cf.fieldComponent } : null,
          componentId: cf?.componentId ?? matchedComponent?.id,
          componentName: matchedComponent?.name,
          component: null,
          alignment: 0,
          isRequired: cf?.fieldComponent?.config?.uiMeta?.isRequired || false
        }));

        const newRow: FormRow = {
          id: `row-${Date.now()}`,
          layout: 'MULTIPLE',
          formFields: componentFormFields
        };

        if (insertNewRow || !isMainDropZone) {
          updatedRows = [...updatedRows.slice(0, rowIndex), newRow, ...updatedRows.slice(rowIndex)];
        } else {
          updatedRows = [...updatedRows, newRow];
        }
      } else if (isFieldDrop && matchedField) {
        const formField: FormFieldTest = {
          id: droppedEntityId,
          field: { ...matchedField },
          component: null,
          alignment: alignment || 0,
          isRequired: matchedField?.config?.uiMeta?.isRequired || false
        };

        if (!alignment) {
          const newRow: FormRow = {
            id: `row-${Date.now()}`,
            layout: 'SINGLE',
            formFields: [formField]
          };

          updatedRows = isMainDropZone
            ? [...updatedRows, newRow]
            : [...updatedRows.slice(0, rowIndex), newRow, ...updatedRows.slice(rowIndex)];
        } else {
          if (insertNewRow) {
            const newRow: FormRow = {
              id: `row-${Date.now()}`,
              layout: 'MULTIPLE',
              formFields: [{ ...formField, alignment }]
            };
            updatedRows = [...updatedRows.slice(0, rowIndex), newRow, ...updatedRows.slice(rowIndex)];
          } else if (!isMainDropZone) {
            const existingRow = { ...updatedRows[rowIndex] };

            // Disallow dropping into component rows
            const isComponent = (
              existingRow.layout === 'MULTIPLE' &&
              Array.isArray(existingRow.formFields) &&
              existingRow.formFields.length > 0 &&
              existingRow.formFields.every((f: any) => (f?.alignment ?? 0) === 0) &&
              existingRow.formFields.some((f: any) => f?.componentId || f?.component || f?.componentName)
            );
            if (isComponent) {
              showToast("Not Allowed", "Cannot drop inside a component block", "error");
              return prevPages;
            }

            if (existingRow.layout === 'MULTIPLE') {
              const positionTaken = existingRow?.formFields.some((f: FormFieldTest) => f?.alignment === alignment);
              if (positionTaken) {
                showToast("Cannot Add", "This position is already occupied", "error");
                return prevPages;
              }

              updatedRows[rowIndex] = {
                ...existingRow,
                formFields: [...existingRow.formFields, { ...formField, alignment }]
              };
            } else {
              updatedRows[rowIndex] = {
                id: existingRow?.id,
                layout: 'MULTIPLE',
                formFields: [
                  { ...existingRow.formFields[0], alignment: 1 },
                  { ...formField, alignment: 2 }
                ]
              };
            }
          } else {
            updatedRows.push({
              id: `row-${Date.now()}`,
              layout: 'MULTIPLE',
              formFields: [{ ...formField, alignment }]
            });
          }
        }
      } else {
        // Unknown type or conflicting state – ignore safely
        return prevPages;
      }

      updatedPage.formRows = updatedRows;
      updatedPages[pageIndex] = updatedPage;
      return updatedPages;
    });

    if (droppingAtEnd) {
      setTimeout(() => {
        if (formAreaRef.current) {
          formAreaRef.current.scrollTo({
            top: formAreaRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [fields, components, showToast, formPages]);

  // When a row has a single full-width field and a user drops
  // another field directly on that row/field, convert the row
  // into a two-column layout: existing -> left, dropped -> right.
  const handleDropOnSingleRow = useCallback((
    e: React.DragEvent<HTMLDivElement>,
    pageIndex: number,
    rowIndex: number
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedType = e.dataTransfer.getData("entityType") || e.dataTransfer.getData("type");
    if (droppedType && droppedType !== 'field') return; // only handle fields here
    const droppedEntityIdStr = e.dataTransfer.getData("id");
    if (!droppedEntityIdStr) return; // not a field drop
    const droppedEntityId = Number(droppedEntityIdStr);

    // Only handle fields (ignore components and row drags for this behavior)
    const fieldToAdd = fields.find(f => f?.id === droppedEntityId);
    // If type was unknown, and both a component and a field share id, prefer field for this handler
    if (!fieldToAdd) return;

    setPages(prevPages => {
      const updatedPages = JSON.parse(JSON.stringify(prevPages)) as FormPage[];
      const page = updatedPages[pageIndex];
      if (!page) return prevPages;
      const row = page.formRows[rowIndex];
      if (!row) return prevPages;

      // Only convert when it's a SINGLE layout with exactly one field
      if (row.layout !== 'SINGLE' || !row.formFields || row.formFields.length !== 1) {
        return prevPages;
      }

      const existing = row.formFields[0];
      if (!existing?.field) {
        // If it's not a standard single field (e.g., component), ignore
        return prevPages;
      }

      const newFormField: FormFieldTest = {
        id: fieldToAdd.id!,
        field: { ...fieldToAdd },
        component: null,
        alignment: 2,
        isRequired: fieldToAdd?.config?.uiMeta?.isRequired || false
      };

      // Update the row to MULTIPLE: existing -> left(1), new -> right(2)
      row.layout = 'MULTIPLE';
      existing.alignment = 1;
      row.formFields = [existing, newFormField];

      return updatedPages;
    });

    setIsDragging(false);
    setDraggedFieldKey(null);
  }, [fields]);

  const handleDeleteField = useCallback((pageIndex: number, rowIndex: number, fieldId: number) => {
    setPages(prevPages => {
      const updatedPages = JSON.parse(JSON.stringify(prevPages));
      const updatedPage = { ...updatedPages[pageIndex] };
      const updatedRows = [...updatedPage.formRows];
      const rowToUpdate = { ...updatedRows[rowIndex] };

      const fieldIndex = rowToUpdate.formFields.findIndex((f: FormFieldTest) =>
        f?.id === fieldId || (f.field?.id === fieldId) || (f?.componentId === fieldId)
      );

      if (fieldIndex === -1) return prevPages;

      const updatedFields = [
        ...rowToUpdate.formFields.slice(0, fieldIndex),
        ...rowToUpdate.formFields.slice(fieldIndex + 1)
      ];

      rowToUpdate.formFields = updatedFields;

      if (rowToUpdate.layout === 'MULTIPLE' && updatedFields.length === 1) {
        rowToUpdate.layout = 'SINGLE';
        updatedFields[0] = { ...updatedFields[0], alignment: 0 };
      }

      if (updatedFields.length === 0) {
        updatedRows.splice(rowIndex, 1);
      } else {
        updatedRows[rowIndex] = rowToUpdate;
      }

      updatedPage.formRows = updatedRows;
      updatedPages[pageIndex] = updatedPage;
      return updatedPages;
    });

    setSelectedRow(null);
  }, []);

  const handleDeleteRow = useCallback((pageIndex: number, rowIndex: number) => {
    setPages(prev => {
      const newPages = [...prev];
      const page = { ...newPages[pageIndex] };

      page.formRows = [
        ...page.formRows.slice(0, rowIndex),
        ...page.formRows.slice(rowIndex + 1)
      ];

      newPages[pageIndex] = page;
      return newPages;
    });
    setSelectedRow(null);
  }, []);


  useEffect(() => {
    const handleClickOutside = () => {
      setSelectedRow(null);
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);


  const prepareFormData = useCallback(() => ({
    id: formId,
    name: formTitle,
    title: formTitle,
    formPages: formPages.map(page => ({
      title: page.title,
      description: page.description,
      formRows: page.formRows.map(row => ({
        layout: row.layout,
        formFields: row.formFields
          .filter(formField => formField?.field?.id)
          .map(formField => {
            const resolvedComponentId = formField?.componentId
              ?? (formField as any)?.component?.id
              ?? (formField as any)?.component?.componentId;

            const baseField = {
              fieldId: formField?.field?.id,
              componentId: typeof resolvedComponentId === 'number' ? resolvedComponentId : undefined,
              alignment: formField.alignment,
              isRequired: formField.isRequired
            };

            // Include config only for DISPLAY_ONLY fields
            if (formField?.field?.dataType === FieldDataTypeEnum.DISPLAY_ONLY) {
              return {
                ...baseField,
                config: formField?.field?.config
              };
            }

            return baseField;
          })
      }))
    }))
  }), [formPages, formTitle, formId]);

  // Keep latest prepare function in a ref for manual save
  useEffect(() => {
    prepareFormDataRef.current = prepareFormData;
  }, [prepareFormData]);

  // When the form is dirty, intercept client-side route changes (via
  // the patched `locationchange` event in `ToastContext`) and show the
  // existing ConfirmationPrompt. If the user confirms, perform the
  // pending navigation WITHOUT saving the draft. Also keep a
  // beforeunload handler for refresh/close so the browser native prompt appears.
  const pendingNavigationRef = useRef<{ href?: string; type?: 'link' | 'pop' } | null>(null);
  const allowPopOnceRef = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
      return undefined;
    };

    const handleLocationChange = () => {
      const newPath = window.location.pathname + window.location.search + window.location.hash;
      const currentPath = initialPathRef.current;
      if (!isDirtyRef.current) {
        initialPathRef.current = newPath;
        return;
      }

      if (newPath !== currentPath) {
        try {
          // revert the URL to avoid navigating away
          window.history.pushState(null, document.title, currentPath);
        } catch (err) { }
        pendingNavigationRef.current = { href: newPath, type: 'link' };
        setShowConfirmationPrompt(true);
      }
    };

    const handleDocumentClick = (ev: MouseEvent) => {
      if (!isDirtyRef.current) return;
      if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      let target = ev.target as HTMLElement | null;
      while (target && target !== document.body) {
        if (target instanceof HTMLAnchorElement && target.href) {
          const href = target.getAttribute('href') || '';
          const targetAttr = target.getAttribute('target');
          // Ignore external or new-tab links or anchors
          if (targetAttr === '_blank' || href.startsWith('http') && !href.startsWith(window.location.origin) || href.startsWith('#')) return;
          // Prevent the browser from performing a full navigation (which triggers beforeunload)
          ev.preventDefault();
          // Resolve absolute href for navigate
          const resolved = new URL(target.href, window.location.origin).pathname + new URL(target.href, window.location.origin).search + new URL(target.href, window.location.origin).hash;
          pendingNavigationRef.current = { href: resolved, type: 'link' };
          setShowConfirmationPrompt(true);
          return;
        }
        target = target.parentElement;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('locationchange', handleLocationChange as EventListener);
    document.addEventListener('click', handleDocumentClick);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('locationchange', handleLocationChange as EventListener);
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);


  const handlePublish = useCallback(async () => {
    try {
      // ✅ Check if form has fields
      if (!formPages || formPages.length === 0 || formPages.every(p => !p.formRows || p.formRows.length === 0)) {
        showToast("Warning", "You must add at least one field before publishing.", "error");
        return;
      }

      const formData = prepareFormData();
      const formId = formData?.id;
      if (!formId) {
        showToast("Error", "Missing form ID for publishing.", "error");
        return;
      }

      // First update the form
      await updateCustomForm(formData).unwrap();
      // Clear dirty flag after successful update so navigation won't prompt
      isDirtyRef.current = false;
      initialPathRef.current = window.location.pathname + window.location.search + window.location.hash;

      // Only publish if update succeeded
      await publishCustomForm(formId).unwrap();

      showToast("Form Published", "Form published successfully!", "success");
      navigate(`/home/forms`);
    } catch (error) {
      showToast("Error", "Failed to publish form.", "error");
      console.error("Publish error:", error);
    }
  }, [formPages, formTitle, showToast, navigate, updateCustomForm, publishCustomForm]);



  const handleSaveDraft = useCallback(async () => {
    const formData = prepareFormData();
    try {
      setIsSavingDraft(true);
      await updateCustomForm(formData).unwrap();
      // After a successful draft save, clear dirty flag so navigation won't show confirmation
      isDirtyRef.current = false;
      initialPathRef.current = window.location.pathname + window.location.search + window.location.hash;
      showToast("Draft Saved", "Form draft saved successfully!", "success");
    } catch (error: any) {
      showToast("Save Failed", error?.data?.message || "Something went wrong while saving the draft.", "error");
    }
    finally {
      setIsSavingDraft(false);
    }
  }, [formPages, formTitle, showToast, updateCustomForm]);

  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const allowDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  // Auto-scroll while dragging when pointer is near top/bottom of the form area
  const autoScrollRef = useRef<{ rafId: number | null; active: boolean; direction: number; speed: number }>({ rafId: null, active: false, direction: 0, speed: 0 });

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current.rafId) {
      cancelAnimationFrame(autoScrollRef.current.rafId as number);
      autoScrollRef.current.rafId = null;
    }
    autoScrollRef.current.active = false;
    autoScrollRef.current.direction = 0;
    autoScrollRef.current.speed = 0;
  }, []);

  const handleFormAreaDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // allow drop
    e.preventDefault();
    if (!formAreaRef.current) return;

    const rect = formAreaRef.current.getBoundingClientRect();
    const threshold = 80; // px from top/bottom to start auto-scrolling
    const y = e.clientY;
    const topDist = y - rect.top;
    const bottomDist = rect.bottom - y;

    let direction = 0;
    let speed = 0;

    if (topDist < threshold) {
      direction = -1;
      speed = Math.ceil(((threshold - topDist) / threshold) * 20);
    } else if (bottomDist < threshold) {
      direction = 1;
      speed = Math.ceil(((threshold - bottomDist) / threshold) * 20);
    } else {
      direction = 0;
      speed = 0;
    }

    autoScrollRef.current.direction = direction;
    autoScrollRef.current.speed = speed;

    if (direction !== 0 && !autoScrollRef.current.active) {
      autoScrollRef.current.active = true;

      const step = () => {
        if (!autoScrollRef.current.active || !formAreaRef.current) {
          autoScrollRef.current.rafId = null;
          return;
        }

        if (autoScrollRef.current.direction === 0) {
          // no longer need to scroll
          stopAutoScroll();
          return;
        }

        // scroll by speed (px) each frame - smoothness comes from RAF
        formAreaRef.current.scrollBy({ top: autoScrollRef.current.direction * autoScrollRef.current.speed, left: 0, behavior: 'auto' });

        autoScrollRef.current.rafId = requestAnimationFrame(step);
      };

      autoScrollRef.current.rafId = requestAnimationFrame(step);
    }
  }, [stopAutoScroll]);

  useEffect(() => {
    if (!isDragging) {
      stopAutoScroll();
    }
  }, [isDragging, stopAutoScroll]);

  const promptDeletePage = useCallback((pageIndex: number) => {
    setItemToDelete({
      type: 'page',
      pageIndex,
      label: formPages[pageIndex].title
    });
    setShowDeletePrompt(true);
  }, [formPages]);

  const handleDeletePageConfirmed = useCallback(() => {
    if (!itemToDelete || itemToDelete.type !== 'page' || itemToDelete.pageIndex === undefined) return;
    handleDeletePage(itemToDelete.pageIndex);
    setShowDeletePrompt(false);
    setItemToDelete(null);
  }, [itemToDelete, handleDeletePage]);

  const handleDeleteFieldConfirmed = useCallback(() => {
    if (!itemToDelete ||
      itemToDelete.type !== 'field' ||
      itemToDelete.pageIndex === undefined ||
      itemToDelete.rowIndex === undefined ||
      itemToDelete.fieldIndex === undefined) return;
    handleDeleteField(itemToDelete.pageIndex, itemToDelete.rowIndex, itemToDelete.fieldIndex);
    setShowDeletePrompt(false);
    setItemToDelete(null);
  }, [itemToDelete, handleDeleteField]);

  const handleDeleteRowConfirmed = useCallback(() => {
    if (!itemToDelete ||
      itemToDelete.type !== 'row' ||
      itemToDelete.pageIndex === undefined ||
      itemToDelete.rowIndex === undefined) return;
    handleDeleteRow(itemToDelete.pageIndex, itemToDelete.rowIndex);
    setShowDeletePrompt(false);
    setItemToDelete(null);
  }, [itemToDelete, handleDeleteRow]);

  const handleSaveFieldSettings = useCallback((nextConfig: FieldConfig) => {
    if (!fieldSettingsTarget) return;

    setPages(prevPages => prevPages.map((page, pageIndex) => {
      if (pageIndex !== fieldSettingsTarget.pageIndex) return page;

      return {
        ...page,
        formRows: page.formRows.map((row, rowIndex) => {
          if (rowIndex !== fieldSettingsTarget.rowIndex) return row;

          return {
            ...row,
            formFields: row.formFields.map((formField, fieldIndex) => {
              if (fieldIndex !== fieldSettingsTarget.fieldIndex) return formField;
              if (!formField.field) return formField;

              const resolvedConfig = {
                ...(formField.config || {}),
                ...(nextConfig || {}),
              };

              return {
                ...formField,
                config: resolvedConfig,
                field: {
                  ...formField.field,
                  config: resolvedConfig,
                },
              };
            }),
          };
        }),
      };
    }));

    setFieldSettingsOpen(false);
    setFieldSettingsTarget(null);
  }, [fieldSettingsTarget]);

  // Render functions
  const renderField = (
    formField: FormFieldTest,
    fieldIndex: number,
    pageIndex: number,
    rowIndex: number
  ) => {
    // Check if this is a LAYOUT field
    const isLayoutField = formField.field?.groupName?.toUpperCase() === 'LAYOUT';
    const fieldName = formField.field?.name || '';

    const fieldProps = {
      formField,
      isLayoutField,
      fieldName,
      onEdit: () => {
        if (formField.field?.dataType === FieldDataTypeEnum.DISPLAY_ONLY) {
          setFieldSettingsTarget({
            pageIndex,
            rowIndex,
            fieldIndex,
            field: formField,
          });
          setFieldSettingsOpen(true);
          return;
        }

        // Open settings for the field
        showToast("Settings", "Field settings can be configured here", "info");
        console.log("Edit field settings:", formField);
      },
      onDelete: () => {
        const fieldId = formField?.id || formField.field?.id || formField.componentId;
        if (!fieldId) return;

        setItemToDelete({
          type: 'field',
          pageIndex,
          rowIndex,
          fieldIndex: fieldId,
          label: formField.field?.name || formField.component?.name || 'Field'
        });
        setShowDeletePrompt(true);
      },
      onCopy: () => {
        // Duplicate layout fields in-place; duplicate DISPLAY_ONLY fields into a new row below.
        if (formField.field?.dataType === FieldDataTypeEnum.DISPLAY_ONLY) {
          setPages(prevPages => {
            const updatedPages = JSON.parse(JSON.stringify(prevPages));
            const updatedPage = { ...updatedPages[pageIndex] };
            const updatedRows = [...updatedPage.formRows];
            const rowToUpdate = { ...updatedRows[rowIndex] };

            const duplicatedField: FormFieldTest = {
              ...formField,
              id: Date.now(),
              alignment: 0,
              component: null,
              componentId: undefined,
              config: formField?.config ? JSON.parse(JSON.stringify(formField.config)) : formField?.config,
              field: formField.field ? {
                ...formField.field,
                config: formField.field.config ? JSON.parse(JSON.stringify(formField.field.config)) : formField.field.config,
              } : null,
            };

            const duplicatedRow: FormRow = {
              id: `row-${Date.now()}`,
              layout: 'SINGLE',
              formFields: [duplicatedField],
            };

            updatedRows.splice(rowIndex + 1, 0, duplicatedRow);
            updatedPage.formRows = updatedRows;
            updatedPages[pageIndex] = updatedPage;

            return updatedPages;
          });
          showToast("Duplicated", "Display-only field duplicated successfully", "success");
          return;
        }

        if (isLayoutField) {
          setPages(prevPages => {
            const updatedPages = JSON.parse(JSON.stringify(prevPages));
            const updatedPage = { ...updatedPages[pageIndex] };
            const updatedRows = [...updatedPage.formRows];
            const rowToUpdate = { ...updatedRows[rowIndex] };

            // Create a new field with the same properties
            const newField: FormFieldTest = {
              ...formField,
              id: Date.now() // Generate unique ID for the duplicate
            };

            // Add the new field after the current one
            rowToUpdate.formFields = [...rowToUpdate.formFields, newField];
            updatedRows[rowIndex] = rowToUpdate;
            updatedPage.formRows = updatedRows;
            updatedPages[pageIndex] = updatedPage;
            return updatedPages;
          });
          showToast("Duplicated", "Field duplicated successfully", "success");
        }
      },
      onToggleRequired: (isRequired: boolean) => {
        setPages(prevPages => prevPages.map((page, pIdx) =>
          pIdx === pageIndex
            ? {
                ...page,
                formRows: page.formRows.map((row, rIdx) =>
                  rIdx === rowIndex
                    ? {
                        ...row,
                        formFields: row.formFields.map((ff, fIdx) =>
                          fIdx === fieldIndex ? { ...ff, isRequired } : ff
                        )
                      }
                    : row
                )
              }
            : page
        ));
      },
      onSelectRow: () => {
        setSelectedRow({ pageIndex, rowIndex });
      },
    };
    if (!formField.field) return null;
    return <FieldPreview {...fieldProps} />;
  };

  const renderRow = (row: FormRow, rowIndex: number, pageIndex: number) => {
    const isRowSelected = selectedRow?.pageIndex === pageIndex && selectedRow?.rowIndex === rowIndex;
    const isLastRow = formPages[pageIndex]?.formRows.length - 1 === rowIndex;
    const gapAboveActive = isDragging && hoveredDropZone?.pageIndex === pageIndex && hoveredDropZone.dropIndex === rowIndex;

    const handleDragStartRow = (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = 'move';
      setDragMode('row');
      setRowDragIndex(rowIndex);
      setRowDragPageIndex(pageIndex);
      // Do not set an insertion index yet; wait until pointer crosses a boundary
      setRowInsertionIndex(null);
      setRowDragHasMoved(false);
      setIsDraggingRow(true);
    };

    const handleDragOverRow = (e: React.DragEvent) => {
      e.preventDefault();
    };

    const handleRowContainerDragOver = (e: React.DragEvent) => {
      if (dragMode !== 'row') return;
      e.preventDefault();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const y = e.clientY - rect.top;
      const half = rect.height / 2;
      // If pointer is in top half -> insertion before this row, else after
      const insertionIdx = y < half ? rowIndex : rowIndex + 1;
      setRowInsertionIndex(insertionIdx);
      // Mark as moved only if insertion differs from original no-op zones
      if (rowDragIndex !== null && insertionIdx !== rowDragIndex && insertionIdx !== rowDragIndex + 1) {
        if (!rowDragHasMoved) setRowDragHasMoved(true);
      }
    };

    const handleRowContainerDrop = (e: React.DragEvent) => {
      if (dragMode !== 'row') return;
      e.preventDefault();
      if (rowInsertionIndex !== null) finalizeRowDrop(pageIndex, rowInsertionIndex);
      setIsDraggingRow(false);
      setDragMode(null);
      setRowDragIndex(null);
      setRowDragPageIndex(null);
      setRowInsertionIndex(null);
    };

    const handleRowContainerDragLeave = (e: React.DragEvent) => {
      // Only clear if truly leaving the row (not entering a child)
      const current = e.currentTarget as Node;
      const next = e.relatedTarget as Node | null;
      const trulyLeaving = !next || !current.contains(next);

      if (dragMode === 'row') {
        if (trulyLeaving) {
          // keep last insertion index until drop for better UX
        }
      } else {
        // Field/component drag: clear single-row hover highlight
        if (trulyLeaving) setHoverSingleRow(null);
      }
    };

    // Show dropzone above/below this row only when hovered while dragging fields/components
    const handleDragOverAbove = (e: React.DragEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      if (dragMode !== 'row') {
        const prevRow = formPages[pageIndex]?.formRows[rowIndex - 1];
        const nextRow = formPages[pageIndex]?.formRows[rowIndex];
        const prevKey = getComponentGroupKey(prevRow);
        const nextKey = getComponentGroupKey(nextRow);
        if (prevKey && nextKey && prevKey === nextKey) return; // inside component block
      }
      setHoveredDropZone({ pageIndex, dropIndex: rowIndex });
    };
    const handleDragLeaveAbove = (e: React.DragEvent) => {
      const current = e.currentTarget as Node;
      const next = e.relatedTarget as Node | null;
      if (!next || !current.contains(next)) {
        setHoveredDropZone(null);
      }
    };
    const handleDragOverBelow = (e: React.DragEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      if (dragMode !== 'row') {
        const prevRow = formPages[pageIndex]?.formRows[rowIndex];
        const nextRow = formPages[pageIndex]?.formRows[rowIndex + 1];
        const prevKey = getComponentGroupKey(prevRow);
        const nextKey = getComponentGroupKey(nextRow);
        if (prevKey && nextKey && prevKey === nextKey) return; // inside component block
      }
      setHoveredDropZone({ pageIndex, dropIndex: rowIndex + 1 });
    };
    const handleDragLeaveBelow = (e: React.DragEvent) => {
      const current = e.currentTarget as Node;
      const next = e.relatedTarget as Node | null;
      if (!next || !current.contains(next)) {
        setHoveredDropZone(null);
      }
    };
    // Component-based multi-field row (all alignments 0) rendering branch
    if (row.layout === 'MULTIPLE' && row.formFields.every(f => f.alignment === 0)) {
      return (
        <div style={{ position: 'relative' }}>
          {/* Hover-sensitive dropzone above this row */}
          <div onDragOver={handleDragOverAbove} onDragLeave={handleDragLeaveAbove} style={{ minHeight: 6 }}>
            {renderInsertDropZones(pageIndex, rowIndex)}
          </div>
          <FormRow_
            theme={theme}
            $isActive={isRowSelected}
            $isDraggable={true}
            $isDragging={isDraggingRow}
            $gapAbove={gapAboveActive}
            $suppressInlineGap={true}
            $isComponent
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRow({ pageIndex, rowIndex });
            }}
            draggable
            onDragStart={handleDragStartRow}
            onDragOver={(e) => {
              // Preserve field/component hover logic
              handleDragOverRow(e);
              if (dragMode === 'row') handleRowContainerDragOver(e);
              // Do not set inline hoveredDropZone for non-row drags inside component blocks
            }}
            onDragLeave={handleRowContainerDragLeave}
            onDrop={handleRowContainerDrop}
          >
            <div className="col-12">
              <div className="component-container p-0 pb-1" style={{ background: theme.CorporateTheme.colors.formBuilderNormalBG }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-2 ms-2 mt-2">
                    
                    <StyledText fontSize="16px" fontWeight={600}>
                      {row.formFields[0]?.component?.name || row.formFields[0]?.componentName}
                    </StyledText><ComponentBadge>Component</ComponentBadge>
                  </div>
                  {isRowSelected && (
                    <IconButton onClick={() => {
                      setItemToDelete({
                        type: 'row',
                        pageIndex,
                        rowIndex,
                        label: row.formFields[0]?.component?.name || 'Component'
                      });
                      setShowDeletePrompt(true);
                    }} title="Delete"
                      theme={theme}
                    >
                      <DeleteIcon size={16} />
                    </IconButton>
                  )}
                </div>
                <div className="component-fields-grid">
                  {row.formFields.map((formField, fieldIndex) => (
                    <div key={`${row?.id}-${fieldIndex}`} className="component-field">
                      {renderField(formField, fieldIndex, pageIndex, rowIndex)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FormRow_>
          {/* For last row, provide below dropzone */}
          {formPages[pageIndex]?.formRows.length - 1 === rowIndex && (
            <div onDragOver={handleDragOverBelow} onDragLeave={handleDragLeaveBelow} style={{ minHeight: 10 }}>
              {renderInsertDropZones(pageIndex, rowIndex + 1)}
            </div>
          )}
        </div>
      );
    }

    const leftField = row.formFields.find(f => f.alignment === 1);
    const rightField = row.formFields.find(f => f.alignment === 2);

    // Drag-and-drop handlers for swapping left/right fields
    const handleDragStartField = (e: React.DragEvent, alignment: 1 | 2) => {
      e.dataTransfer.setData("fieldAlignment", String(alignment));
      e.dataTransfer.setData("rowIndex", String(rowIndex));
      e.dataTransfer.setData("pageIndex", String(pageIndex));
    };

    const handleDropField = (e: React.DragEvent, targetAlignment: 1 | 2) => {
      e.preventDefault();
      const sourceAlignment = Number(e.dataTransfer.getData("fieldAlignment"));
      const srcRowIndex = Number(e.dataTransfer.getData("rowIndex"));
      const srcPageIndex = Number(e.dataTransfer.getData("pageIndex"));
      // Only allow swapping within the same row
      if (srcRowIndex === rowIndex && srcPageIndex === pageIndex && sourceAlignment !== targetAlignment) {
        setPages(prevPages => {
          const newPages = [...prevPages];
          const page = { ...newPages[pageIndex] };
          const rows = [...page.formRows];
          const rowToUpdate = { ...rows[rowIndex] };
          const fields = [...rowToUpdate.formFields];
          const leftIdx = fields.findIndex(f => f.alignment === 1);
          const rightIdx = fields.findIndex(f => f.alignment === 2);
          if (leftIdx !== -1 && rightIdx !== -1) {
            // Swap alignment values
            fields[leftIdx].alignment = 2;
            fields[rightIdx].alignment = 1;
            rowToUpdate.formFields = fields;
            rows[rowIndex] = rowToUpdate;
            page.formRows = rows;
            newPages[pageIndex] = page;
            return newPages;
          }
          return prevPages;
        });
      }
    };

    return (
      <div style={{ position: 'relative' }}>
        {/* Hover-sensitive dropzone above this row */}
        <div onDragOver={handleDragOverAbove} onDragLeave={handleDragLeaveAbove} style={{ minHeight: 6 }}>
          {renderInsertDropZones(pageIndex, rowIndex)}
        </div>
        <FormRow_
          theme={theme}
          $isActive={isRowSelected}
          $isDraggable={true}
          $isDragging={isDraggingRow}
          $isHovered={
            !!hoverSingleRow &&
            hoverSingleRow.pageIndex === pageIndex &&
            hoverSingleRow.rowIndex === rowIndex &&
            row.layout === 'SINGLE' &&
            dragMode !== 'row'
          }
          $suppressInlineGap={false}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedRow({ pageIndex, rowIndex });
          }}
          draggable
          onDragStart={handleDragStartRow}
          onDragOver={(e) => {
            handleDragOverRow(e);
            if (dragMode === 'row') handleRowContainerDragOver(e);
            else if (isDragging) {
              setHoveredDropZone({ pageIndex, dropIndex: rowIndex });
              if (row.layout === 'SINGLE') setHoverSingleRow({ pageIndex, rowIndex });
            }
          }}
          onDragLeave={handleRowContainerDragLeave}
          onDrop={(e) => {
            // Clear hover hint when dropping
            setHoverSingleRow(null);
            handleRowContainerDrop(e);
          }}
        >
          {isRowSelected && (
            <RowDeleteButton
              onClick={(e) => {
                e.stopPropagation();
                setItemToDelete({
                  type: 'row',
                  pageIndex,
                  rowIndex,
                  label: 'Row',
                });
                setShowDeletePrompt(true);
              }}
            >
              <DeleteIcon size={16} />
            </RowDeleteButton>
          )}

          <div className={row.layout === 'SINGLE' ? 'col-12' : 'col-6'}>
            {row.layout === 'SINGLE' ? (
              row.formFields[0] && (
                <RowFieldWrapper
                  onDragOver={allowDrop}
                  onDrop={(e) => {
                    setHoverSingleRow(null);
                    handleDropOnSingleRow(e, pageIndex, rowIndex);
                  }}
                >
                  {renderField(row.formFields[0], 0, pageIndex, rowIndex)}
                </RowFieldWrapper>
              )
            ) : (
              <>
                {leftField && (
                  <RowFieldWrapper
                    draggable
                    onDragStart={(e) => handleDragStartField(e, 1)}
                    onDragOver={allowDrop}
                    onDrop={(e) => handleDropField(e, 1)}
                  >
                    {renderField(leftField, 0, pageIndex, rowIndex)}
                  </RowFieldWrapper>
                )}
              </>
            )}
          </div>

          {row.layout === 'MULTIPLE' && (
            <div className="col-6">
              {rightField && (
                <RowFieldWrapper
                  draggable
                  onDragStart={(e) => handleDragStartField(e, 2)}
                  onDragOver={allowDrop}
                  onDrop={(e) => handleDropField(e, 2)}
                >
                  {renderField(rightField, 1, pageIndex, rowIndex)}
                </RowFieldWrapper>
              )}
            </div>
          )}
        </FormRow_>
        {/* For last row, provide below dropzone */}
        {formPages[pageIndex]?.formRows.length - 1 === rowIndex && (
          <div onDragOver={handleDragOverBelow} onDragLeave={handleDragLeaveBelow} style={{ minHeight: 10 }}>
            {renderInsertDropZones(pageIndex, rowIndex + 1)}
          </div>
        )}
      </div>
    );
  };

  const renderFieldSkeletons = useCallback((count: number = 8) => {
    return Array.from({ length: count }).map((_, index) => (
      <SkeletonFieldChip key={`skeleton-${index}`}>
        <SkeletonCell height="40px" />
      </SkeletonFieldChip>
    ));
  }, []);

  const renderPageButtons = (pageIndex: number) => (
    formPages.length > 1 && selectedPageIndex === pageIndex && (
      <PageButtons className="d-flex align-items-center gap-1">
        <AddWhiteIcon
          size={20}
          className="text-white"
          onClick={handleAddPage}
        />
        <CrossIcon
          size={18}
          className="text-white"
          onClick={() => promptDeletePage(pageIndex)}
        />
      </PageButtons>
    )
  );

  // (Removed bottom sticky drop zones with left/right columns.)

  // Helpers to detect component rows and grouping
  const isComponentRow = useCallback((row: FormRow | undefined | null) => {
    if (!row) return false;
    if (!Array.isArray(row.formFields) || row.formFields.length === 0) return false;
    // Consider any row with at least one field carrying component metadata as a component row
    return row.formFields.some((f: any) => f?.componentId || f?.component || f?.componentName);
  }, []);

  const getComponentGroupKey = useCallback((row: FormRow | undefined | null) => {
    if (!row || !isComponentRow(row)) return null;
    const ff: any = row.formFields.find((f: any) => f?.componentId || f?.component || f?.componentName);
    return String(ff?.componentId ?? ff?.component?.id ?? ff?.componentName ?? '');
  }, [isComponentRow]);

  // Inline drop zones to insert before first row and between rows
  const renderInsertDropZones = useCallback((pageIndex: number, insertionIndex: number) => {
    // Show for field/component dragging OR row dragging (dragMode==='row')
    if (!isDragging && dragMode !== 'row') return null;

    const rowCount = formPages[pageIndex]?.formRows.length || 0;
    const isFirst = rowCount === 0 && insertionIndex === 0;
    const isAfterLast = rowCount > 0 && insertionIndex === rowCount;
    const forceVisible = isFirst; // always show for first insertion while dragging, even without hover

     // Suppress dropzones that would appear between rows belonging to the same component block
     if (!isFirst && !isAfterLast) {
       const prevRow = formPages[pageIndex]?.formRows[insertionIndex - 1];
       const nextRow = formPages[pageIndex]?.formRows[insertionIndex];
       const prevKey = getComponentGroupKey(prevRow);
       const nextKey = getComponentGroupKey(nextRow);
       if (prevKey && nextKey && prevKey === nextKey) return null;
     }

    // Require hover unless it's the initial empty insertion
    const isRowDragActive = dragMode === 'row';
    const matchesRowInsertion = isRowDragActive && rowInsertionIndex === insertionIndex;
    if (!forceVisible && !matchesRowInsertion && (!hoveredDropZone || hoveredDropZone.pageIndex !== pageIndex || hoveredDropZone.dropIndex !== insertionIndex)) return null;

    // For row dragging: suppress showing insertion drop zone until user actually moves beyond original row bounds
    if (dragMode === 'row' && !rowDragHasMoved) {
      const isOriginalBoundary = insertionIndex === rowDragIndex || insertionIndex === (rowDragIndex ?? -1) + 1;
      if (!forceVisible && isOriginalBoundary) return null;
    }

    // Derive dynamic height from form area for better coverage
    let dynamicHeight: string | undefined;
    if ((isFirst || isAfterLast) && formAreaRef.current) {
      const areaH = formAreaRef.current.clientHeight;
      dynamicHeight = `${Math.max(160, areaH - 40)}px`;
    }

    return (
      <div key={`dropzones-${pageIndex}-${insertionIndex}`}>
        <DropArea
          $isActive
          $fullHeight={isFirst || isAfterLast}
          style={dynamicHeight ? { height: dynamicHeight } : undefined}
          onDrop={(e) => {
            if (dragMode === 'row') {
              e.preventDefault();
              finalizeRowDrop(pageIndex, insertionIndex);
              setIsDraggingRow(false);
              setDragMode(null);
              setRowDragIndex(null);
              setRowDragPageIndex(null);
              setRowInsertionIndex(null);
            } else {
              handleDrop(e, pageIndex, insertionIndex, 0, true);
            }
            setIsDraggingOver(null);
            setIsDragging(false);
            setHoveredDropZone(null);
          }}
          onDragOver={(e) => {
            allowDrop(e);
            if (dragMode === 'row') setRowInsertionIndex(insertionIndex);
            else setHoveredDropZone({ pageIndex, dropIndex: insertionIndex });
          }}
          onDragLeave={(e) => {
            // For first insertion we keep visible regardless of hover state
            if (forceVisible) return;
            const current = e.currentTarget as Node;
            const next = e.relatedTarget as Node | null;
            if (!next || !current.contains(next)) {
              if (dragMode === 'row') {
                // keep last insertion index until drop for better UX
              } else setHoveredDropZone(null);
            }
          }}
        >
          {(isFirst || (isAfterLast && !dragMode)) && (
            <StyledText fontSize="14px" fontWeight={600} color={theme.CreateFormTheme?.colors?.primary}>
              Drop here to add your {isFirst ? 'first' : 'next'} field
            </StyledText>
          )}
          {dragMode === 'row' && (
            <StyledText fontSize="12px" fontWeight={600} color={theme.CreateFormTheme?.colors?.primary}>
              Move row here
            </StyledText>
          )}
        </DropArea>
      </div>
    );
  }, [allowDrop, handleDrop, hoveredDropZone, isDragging, formPages, dragMode, rowInsertionIndex]);

  const renderPageStepperSkeleton = () => (
    <PageStepperContainer theme={theme}>
      {[1, 2].map((i) => (
        <SkeletonPageStep key={i}>
          <SkeletonCell height="20px" width="40px" />
        </SkeletonPageStep>
      ))}
    </PageStepperContainer>
  );

  const renderPageSkeleton = () => (
    <PageContainer theme={theme}>
      <PageHeader className="d-flex flex-column p-3 position-relative">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <SkeletonCell height="40px" className="me-2" />
            <SkeletonCell height="40px" />
          </div>
          <SkeletonCell height="40px" />
        </div>
      </PageHeader>
      <StyledHr theme={theme} />
      {[1, 2, 3].map((i) => (
        <SkeletonRow key={i} className="m-3">
          <SkeletonCell height="40px" />
        </SkeletonRow>
      ))}
    </PageContainer>
  );

  const tabs = useMemo(
    () => FORM_BUILDER_TABS.map(tab => ({ ...tab, active: tab.title === activeTab })),
    [activeTab]
  );

  const handleTabChange = useCallback((tabId: number) => {
    setSearchTerm('');
    const tab = FORM_BUILDER_TABS.find(t => t?.id === tabId);
    if (tab) setActiveTab(tab.title);
  }, []);

  // Horizontal scroll controls for tabs
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const el = tabsContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons as any);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [updateScrollButtons]);

  const scrollTabsBy = (dir: number) => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const amount = Math.min(240, Math.floor(el.clientWidth * 0.7));
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
    // update visibility shortly after scrolling
    setTimeout(updateScrollButtons, 250);
  };

  const usedFieldIds = useMemo(() => {
    const ids = new Set<number>();
    formPages.forEach(page => {
      (page.formRows || []).forEach(row => {
        (row.formFields || []).forEach((ff: any) => {
          if (ff?.field?.id) ids.add(Number(ff.field.id));
        });
      });
    });
    return ids;
  }, [formPages]);

  const usedComponentIds = useMemo(() => {
    const ids = new Set<number>();
    formPages.forEach(page => {
      (page.formRows || []).forEach(row => {
        (row.formFields || []).forEach((ff: any) => {
          if (ff?.componentId) ids.add(Number(ff.componentId));
        });
      });
    });
    return ids;
  }, [formPages]);

  const chips = useMemo(() => {
    // Special-case: UI Elements tab should show only UI display fields
    if (activeTab === 'UI Elements') {
      return fields
        .filter(field => {
          const matchesSearch = field.name?.toLowerCase().includes(searchTerm.toLowerCase());
          const isLayout = (field.groupName || '').toUpperCase() === 'LAYOUT';
          return matchesSearch && isLayout;
        }).reduce((grouped: Record<string, Field[]>, field) => {
          const groupName = field.groupName || 'LAYOUT';
          if (!grouped[groupName]) grouped[groupName] = [];
          grouped[groupName].push(field);
          return grouped;
        }, {});
    }

    if (activeTab === 'Custom Fields' || activeTab === 'System Fields') {
      return fields
        .filter(field => {
          // filter by search and field type (system/custom)
          const matchesSearch = field.name?.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesTab = activeTab === 'System Fields' ? field.isStandard : !field.isStandard;
          // filter out fields that are already used in the form
          const isUsed = field?.id ? usedFieldIds.has(Number(field.id)) : false;
          return matchesSearch && matchesTab && !isUsed;
        }).reduce((grouped: Record<string, Field[]>, field) => {
          const groupName = field.groupName || 'Custom';
          if (!grouped[groupName]) grouped[groupName] = [];
          grouped[groupName].push(field);
          return grouped;
        }, {});
    }
    else if (activeTab === 'Components') {
      return components
        .filter(component => {
          const matchesSearch = component.name?.toLowerCase().includes(searchTerm.toLowerCase());
          const isUsed = component?.id ? usedComponentIds.has(Number(component.id)) : false;
          return matchesSearch && !isUsed;
        })
        .reduce((grouped: Record<string, Component[]>, component) => {
          const groupName = component.isStandard ? 'System' : 'Custom';
          if (!grouped[groupName]) grouped[groupName] = [];
          grouped[groupName].push(component);
          return grouped;
        }, {});
    }
    return {};
  }, [fields, components, searchTerm, activeTab, usedFieldIds, usedComponentIds]);

  const getChipIcon = useCallback((chip: Field) => {
    const dataType = String(chip?.dataType || '').toUpperCase();
    const fieldType = String(chip?.fieldType || '').toUpperCase();

    if (dataType === FieldDataTypeEnum.DISPLAY_ONLY || ['HEADING', 'PARAGRAPH', 'DIVIDER'].includes(fieldType)) {
      if (fieldType === 'PARAGRAPH') {
        return <FieldChipParagraphIcon size={16} color={theme.CreateFormTheme.colors.primary} />;
      }

      if (fieldType === 'DIVIDER') {
        return <FieldChipSeparatorIcon size={16} color={theme.CreateFormTheme.colors.primary} />;
      }

      return <FieldChipHeadingIcon size={16} color={theme.CreateFormTheme.colors.primary} />;
    }

    return getIconForDataType(chip?.dataType, { size: 16, color: theme.CreateFormTheme.colors.primary });
  }, [theme]);

  if (isFormError) {
    return (
      <EmptyFormState>
        <StyledText fontSize="18px" fontWeight={600} color={CreateFormTheme.colors.headerColor}>
          Error loading form
        </StyledText>
        <StyledText fontSize="14px" color={CreateFormTheme.colors.textSecondary}>
          Could not load the requested form
        </StyledText>
        <Button
          theme={theme}
          type="button"
          $variant="normal"
          onClick={() => navigate('/home/forms')}
          className="mt-3"
        >
          Back to Forms
        </Button>
      </EmptyFormState>
    );
  }


  return (
    <>
      {showEditFormModal && formId && (
        <CreateFormModal
          isOpen={showEditFormModal}
          onClose={() => setShowEditFormModal(false)}
          mode="edit"
          initialData={{
            id: formId,
            name: formTitle || '',
            description: (form as any)?.description ?? (formsData as any)?.description ?? '',
            scope: (formsData as any)?.scopeId,
            // formPages: formPages,
          }}
          onSuccess={(id, updated) => {
            setShowEditFormModal(false);
            if (updated?.name) setFormTitle(updated.name);
            try { (refetchForm as any)?.(); } catch (e) {}
          }}
        />
      )}

      <FormsHeader theme={theme} className="p-3 pt-0 d-flex align-items-center justify-content-between">
        <div className="d-flex flex-column align-items-start">
          {isLoadingForm ? (
            <SkeletonCell height="40px" />
          ) : (
            <div className="d-flex align-items-center">
              <EditableTitle value={formTitle} onChange={setFormTitle} onPencilClick={() => setShowEditFormModal(true)} scopeLabel={scopeLabel} />
            </div>
          )}
          {savingCount > 0 && (
            <SavingIndicator className="ms-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Drafting Form...
            </SavingIndicator>
          )}
          {!isLoadingForm && (
            <DescriptionRow className="mt-2">
              <DescriptionText $expanded={showFullDescription} theme={theme}>
                {(form as any)?.description ?? (formsData as any)?.description ?? ''}
              </DescriptionText>
              {(((form as any)?.description ?? (formsData as any)?.description ?? '')?.length || 0) > 80 && (
                <button className="view-more-btn" onClick={() => setShowFullDescription(v => !v)}>
                  {showFullDescription ? 'View less' : 'View more'}
                </button>
              )}
            </DescriptionRow>
          )}
        </div>

        <div className="d-flex mt-2 gap-2 align-items-center">
          {/* <StyledToolTip placement="bottom" title="Save as Template">
            <Button
              type="submit"
              $variant="normal"
              onClick={handleSaveDraft}
              disabled={savingCount > 0}
            >
              <FormTempalteIcon color={theme.CorporateTheme.colors.textSecondary} />
            </Button>
          </StyledToolTip>

          <div style={{ width: '1px', height: '24px', backgroundColor: '#EBEAEB' }} /> */}

          <StyledToolTip placement="bottom" title={(publishLoading || updatedCustomerLoading) ? "Cannot save draft while publishing" : "Save as Draft"}>
            {isSavingDraft ? (
              <LoadingSpinner color={theme.CorporateTheme.colors.textSecondary} size="16px" className="me-2" />
            ) : (
              <span
                className="me-2"
                onClick={(!publishLoading && !isSavingDraft && !updatedCustomerLoading) ? handleSaveDraft : undefined}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: (publishLoading || isSavingDraft || updatedCustomerLoading) ? 'not-allowed' : 'pointer',
                  opacity: (publishLoading || isSavingDraft || updatedCustomerLoading) ? 0.6 : 1,
                }}
              >
                <FormDraftIcon color={theme.CorporateTheme.colors.textSecondary} />
              </span>
            )}
          </StyledToolTip>

          <Button
            theme={theme}
            type="button"
            $variant="preview"
            className="border"
            onClick={() => setShowPreview(true)}
          >
            <ViewIcon color={theme.CorporateTheme.colors.textSecondary} />
            Preview
          </Button>

          <Button
            theme={theme}
            type="submit"
            $variant="add"
            onClick={handlePublish}
            disabled={savingCount > 0 || updatedCustomerLoading || publishLoading}
          >
            {publishLoading ? <LoadingSpinner color={theme.CorporateTheme.colors.background} size="18px" /> : <FormsIcon color={theme.CorporateTheme.colors.background} />}
            Publish
          </Button>
          <Button
            theme={theme}
            type="submit"
            $variant="cancel"
            onClick={() => setShowConfirmationPrompt(true)}
            disabled={savingCount > 0}
          >
            <CloseIcon color={theme.CorporateTheme.colors.textSecondary} />
            Cancel
          </Button>
        </div>
      </FormsHeader>

      <Row className="p-0 m-0">
        <SidebarColumn xs={8} md={3} lg={3}>
          <Sidebar theme={theme}>
              <TabsScrollWrapper theme={theme}>
                <TabsScrollInner ref={tabsContainerRef}>
                  <Tabs tabs={tabs} onChangeTabs={handleTabChange} />
                </TabsScrollInner>

                {canScrollLeft && (
                  <FadeEdge $visible={canScrollLeft} $left theme={theme} />
                )}

                {canScrollRight && (
                  <FadeEdge $visible={canScrollRight} theme={theme} />
                )}

                {canScrollLeft && (
                  <ScrollButtonLeft
                    aria-label="Scroll left"
                    onClick={() => scrollTabsBy(-1)}
                    theme={theme}
                  >
                      <LeftArrow color={theme.CreateFormTheme?.colors?.primary || '#595959'} />
                  </ScrollButtonLeft>
                )}

                {canScrollRight && (
                  <ScrollButtonRight
                    aria-label="Scroll right"
                    onClick={() => scrollTabsBy(1)}
                    theme={theme}
                  >
                    <RightArrow color={theme.CreateFormTheme?.colors?.primary || '#595959'} />
                  </ScrollButtonRight>
                )}
              </TabsScrollWrapper>
            <SearchBar
              style={{ width: '100%' }}
              className="mt-2"
              placeholder={activeTab === 'Components' ? "Search components here" : "Search fields here"}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
            <div ref={scrollContainerRef} >
              <SimpleBar autoHide={false} style={{ maxHeight: scrollHeight }}>
                <div className="mt-3 mb-3" style={{ paddingBottom: '60px', overflowX: 'hidden' }}>
                  {isLoadingFields || isFetchingFields || !formsData?.scopeId ? (
                    <FieldGroup className="mb-2 mt-2">
                      <FieldGrid className="d-flex flex-wrap gap-2 mt-2 ms-0">
                        {renderFieldSkeletons()}
                      </FieldGrid>
                    </FieldGroup>
                  ) : Object.keys(chips).length === 0 ? (
                    <NoData
                      title={`No ${activeTab} Found`}
                      subtitle={`No ${activeTab.toLowerCase()} match your search criteria.`} containerHeight={550}
                    />
                  ) : (
                    Object.entries(chips).map(([groupName, chips]) => (
                      <FieldGroup key={groupName} className="mb-2 mt-2">
                        <StyledText
                          className="mb-2 mt-1"
                          fontSize="13px"
                          fontWeight={600}
                          color={theme.CreateFormTheme.colors.headerColor}
                        >
                          {formatLabelWithUnderscore(groupName)}
                        </StyledText>

                        <FieldGrid className="d-flex flex-wrap gap-2 mt-2 ms-0">
                          {(chips as any).map((chip: any) => (
                            <MemoizedFieldChip
                              key={chip.name}
                              icon={activeTab === 'Components' ? <ComponentIconDT size={16} color={theme.CreateFormTheme.colors.primary} /> : getChipIcon(chip)}
                              label={chip.name}
                              isDragging={draggedFieldKey === chip.name}
                              onDragStart={(e) => {
                                e.dataTransfer.setData("id", String(chip?.id));
                                e.dataTransfer.setData("entityType", activeTab === 'Components' ? 'component' : 'field');
                                setDraggedFieldKey(chip?.name ?? null);
                                setIsDragging(true);
                              }}
                              onDragEnd={() => {
                                setDraggedFieldKey(null);
                                setIsDragging(false);
                              }}
                            />
                          ))}
                        </FieldGrid>
                      </FieldGroup>
                    ))
                  )}

                </div>
              </SimpleBar>
            </div>
          </Sidebar>
        </SidebarColumn>

        <FormColumn theme={theme} xs={4} md={9} lg={9}>
          <FormArea
            ref={formAreaRef}
            onClick={() => setSelectedRow(null)}
            onDragOver={handleFormAreaDragOver}
            onDragLeave={() => { /* stop when leaving area */ stopAutoScroll(); }}
            onDragEnd={() => { stopAutoScroll(); setIsDragging(false); setDraggedFieldKey(null); setHoveredDropZone(null); }}
          >
            {isLoadingForm ? (
              renderPageStepperSkeleton()
            ) : (
              <PageStepperContainer theme={theme}>
                {formPages.map((page, index) => (
                  <PageStep
                    key={page?.id}
                    $isActive={selectedPageIndex === index}
                    onClick={() => setSelectedPageIndex(index)}
                  >
                    <FormIcon color={selectedPageIndex === index ? CreateFormTheme.colors.primary : '#ffffff'} size={16} />
                    {page.title}
                    {formPages.length > 1 && (
                      <IconButton
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          promptDeletePage(index);
                        }}
                        title="Delete page"
                      >
                        <CrossIcon color={selectedPageIndex === index ? CreateFormTheme.colors.primary : '#ffffff'} size={16} />
                      </IconButton>
                    )}
                  </PageStep>
                ))}
                <AddPageButton onClick={handleAddPage}>
                  <AddWhiteIcon size={20} />
                  Add Page
                </AddPageButton>
              </PageStepperContainer>
            )}

            {isLoadingForm ? (
              renderPageSkeleton()
            ) : formPages.length === 0 ? (
              <EmptyFormState>
                <StyledText fontSize="18px" fontWeight={600} color={theme.CreateFormTheme.colors.headerColor}>
                  No form found
                </StyledText>
                <StyledText fontSize="14px" color={theme.CreateFormTheme.colors.textSecondary}>
                  Please select a valid form
                </StyledText>
              </EmptyFormState>
            ) : (
              <>
                <PageContainer
                  theme={theme}
                  key={formPages[selectedPageIndex]?.id}
                  className="pb-3 selected-page"
                  ref={el => { dropZoneRefs.current[selectedPageIndex] = el; }}
                >
                  <PageHeader className="d-flex flex-column p-3 position-relative">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <FormIcon color={theme.CorporateTheme.colors.primary} size={20} className="me-2" />
                        <EditableTitle
                          value={formPages[selectedPageIndex]?.title}
                          onChange={(newTitle) => handlePageUpdate(formPages[selectedPageIndex]?.id, { title: newTitle })}
                        />
                      </div>

                      {formPages.length > 1 && (
                        <PageNumber theme={theme}>
                          Page {selectedPageIndex + 1} / {formPages.length}
                        </PageNumber>
                      )}
                    </div>
                    {renderPageButtons(selectedPageIndex)}
                  </PageHeader>

                  <StyledHr theme={theme} />

                  {/* Empty page: hover-sensitive top dropzone for first insertion */}
                  {formPages[selectedPageIndex]?.formRows.length === 0 && isDragging && (
                    <div
                      onDragOver={(e) => { allowDrop(e); setHoveredDropZone({ pageIndex: selectedPageIndex, dropIndex: 0 }); }}
                      onDragLeave={(e) => {
                        const current = e.currentTarget as Node;
                        const next = (e as any).relatedTarget as Node | null;
                        if (!next || !current.contains(next)) {
                          setHoveredDropZone(null);
                        }
                      }}
                      style={{ minHeight: 10 }}
                    >
                      {renderInsertDropZones(selectedPageIndex, 0)}
                    </div>
                  )}

                  {formPages[selectedPageIndex]?.formRows.map((row, rowIndex) => (
                    <React.Fragment key={row?.id || `row-${rowIndex}`}>
                      {renderRow(row, rowIndex, selectedPageIndex)}
                    </React.Fragment>
                  ))}

                  {(formPages[selectedPageIndex]?.formRows.length === 0 && !isDragging) && (
                    <DropZoneText $isActive={isDragging} className="m-3 rounded">
                      <StyledText
                        fontSize="16px"
                        fontWeight={600}
                        color={theme.CreateFormTheme?.colors?.primary}
                        className="text-center mt-4"
                      >
                        {selectedPageIndex === 0 && formPages[selectedPageIndex]?.formRows.length === 0
                          ? 'Start Designing Your Form'
                          : formPages[selectedPageIndex]?.formRows.length === 0
                            ? 'Page Looks Empty!'
                            : 'Keep Designing Your Form'}
                      </StyledText>

                      <StyledText
                        fontSize="14px"
                        fontWeight={500}
                        color={theme.CreateFormTheme.colors.textSecondary}
                        className="mb-4 mt-1 text-center"
                      >
                        Drag fields from left panel and drop here to add them to your form.
                      </StyledText>
                    </DropZoneText>
                  )}
                  {/* Bottom sticky drop zones removed in favor of inline drop zones above/between rows */}
                </PageContainer>
              </>
            )}
          </FormArea>
        </FormColumn>
      </Row>

      <FieldSettingsPanel
        isOpen={fieldSettingsOpen}
        field={fieldSettingsTarget?.field.field || null}
        onClose={() => {
          setFieldSettingsOpen(false);
          setFieldSettingsTarget(null);
        }}
        onSave={handleSaveFieldSettings}
      />

      {showDeletePrompt && itemToDelete && (
        <DeletePrompt
          onConfirm={() => {
            if (itemToDelete.type === 'page') handleDeletePageConfirmed();
            else if (itemToDelete.type === 'row') handleDeleteRowConfirmed();
            else handleDeleteFieldConfirmed();
          }}
          onCancel={() => {
            setShowDeletePrompt(false);
            setItemToDelete(null);
          }}
          title={
            itemToDelete.type === 'page'
              ? `Are you sure you want to delete the "${itemToDelete.label}" page?`
              : `Are you sure you want to delete this ${itemToDelete.type}?`
          }
          subTitle={
            itemToDelete.type === 'page'
              ? "Deleting this page will permanently remove all fields contained within it. This action cannot be undone."
              : "Deleting this will permanently remove it from your form. This action cannot be undone."
          }
        />
      )}

      {showPreview && (
        <FormPreviewModal
          pages={formPages}
          onClose={() => setShowPreview(false)}
          formTitle={formTitle}
        />
      )}
      {showConfirmationPrompt && (
        <ConfirmationPrompt
          title="Cancel Editing?"
          subTitle="You might have unsaved changes. Are you sure you want to leave without saving?"
          onConfirm={() => {
            setShowConfirmationPrompt(false);
            // clear dirty flag so beforeunload handler will not show native prompt
            isDirtyRef.current = false;
            const pending = pendingNavigationRef.current;
            if (pending) {
              pendingNavigationRef.current = null;
              if (pending.type === 'link' && pending.href) {
                // Try SPA navigation first
                try {
                  navigate(pending.href as string);
                } catch (err) {
                  // ignore
                }
                // If SPA navigation didn't change location within short time, force full navigation
                setTimeout(() => {
                  const current = window.location.pathname + window.location.search + window.location.hash;
                  if (!current || (pending.href && !current.startsWith(pending.href))) {
                    try {
                      window.location.assign(pending.href as string);
                    } catch (e) { }
                  }
                }, 120);
                return;
              }
              if (pending.type === 'pop') {
                allowPopOnceRef.current = true;
                // prefer history.back to preserve user intent
                try { window.history.back(); } catch (e) { }
                return;
              }
            }
            navigate(-1);
          }}
          onCancel={() => {
            setShowConfirmationPrompt(false);
            pendingNavigationRef.current = null;
          }}
        />
      )}
    </>
  );
};

// Styled components
const FormsHeader = styled('div')(({ theme }) => ({
  background: theme.CorporateTheme.colors.background,
}));

const SidebarColumn = styled(Col)`
  padding: 0 !important;
`;

const FormColumn = styled(Col)(({ theme }) => ({
  padding: '0 !important',
  backgroundColor: theme.CreateFormTheme.colors.headerBG,
}));

const Sidebar = styled('div')(({ theme }) => ({
  borderRight: `1px solid ${theme.CreateFormTheme.colors.border}`,
  padding: CreateFormTheme.spacing.md,
}));

const FormArea = styled.div`
  flex: 1;
  margin: 1rem 2rem;
  border-radius: 8px;
  position: relative;
  overflow-y: auto;
  max-height: calc(100vh - 220px);
`;

const PageContainer = styled('div')(({ theme }) => ({
  background: theme.CorporateTheme.colors.background,
  borderRadius: '8px',
  marginBottom: '10px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  minHeight: '300px',
  paddingBottom: '20px',

  '&.selected-page': {
    border: '1px solid #0556CB',
    borderRadius: '6px',
  },
}));

const PageHeader = styled.div`
  position: relative;
  padding: 15px;
`;

const PageNumber = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.CreateFormTheme.colors.primary};
`;

const PageButtons = styled.button`
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  width: 55px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  
  &:hover {
    transform: translateX(-50%) scale(1.1);
  }
  
  background-image: url(${PageButtonsBG});
  background-size: contain;
`;

const FieldGroup = styled.div`
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const FieldGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  
  // important: let flex items shrink without overflowing
  & > * {
    min-width: 0;
    max-width: 100%;
  }
`;

const SavingIndicator = styled.div`
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
`;

const DescriptionRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  .view-more-btn {
    border: none;
    background: transparent;
    color: #707070;
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
  }
`;

const DescriptionText = styled.div<{ $expanded?: boolean }>`
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  font-size: 13px;
  max-width: 580px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $expanded }) => ($expanded ? 'unset' : 1)};
  white-space: ${({ $expanded }) => ($expanded ? 'normal' : 'normal')};
`;

const SkeletonFieldChip = styled.div`
  width: 100%;
  height: 40px;
  border-radius: 20px;
  overflow: hidden;
`;

const SkeletonPageStep = styled.div`
  padding: 8px 16px;
  border-radius: 20px;
  background: #f5f5f5;
`;

const SkeletonRow = styled.div`
  border-radius: 8px;
  overflow: hidden;
`;
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
`;

const DropArea = styled.div<{ $isActive?: boolean; $fullHeight?: boolean }>`
  height: ${({ $fullHeight }) => $fullHeight ? 'calc(100vh - 260px)' : '42px'};
  margin: 0 16px;
  border-radius: 4px;
  background: ${({ $isActive }) => $isActive ? '#0556CB26' : '#0000000D'};
  transition: height 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  &:hover {
    background: ${({ theme }) => theme.CorporateTheme?.colors?.dropZoneBG};
  }
`;

const RowFieldWrapper = styled.div`
  width: 100%;
  clear: both;
  position: relative;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 8px 12px;
  box-sizing: border-box;
  gap: 8px;
  overflow: visible;

  /* Ensure inner controls stretch uniformly */
  & > * {
    width: 100%;
  }
`;

const RowDeleteButton = styled.button`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: white;
  border: 1px solid red;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  cursor: pointer;
  
  &:hover {
    background: #f5f5f5;
  }
`;


const DropZoneText = styled.div<{ $isActive?: boolean }>`
  background: ${({ $isActive }) => $isActive ? 'none' : '#0000000D'};
  border: ${({ $isActive }) => $isActive ? 'none' : '1px dashed #BABDC1'};
`;

const ComponentBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #0556CB;
  background: #EAF2FF;
  border: 1px solid #BBD6FF;
  border-radius: 4px;
  padding: 2px 6px;
`;


const EmptyFormState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  text-align: center;
  background: white;
  border-radius: 8px;
  padding: 20px;
`;

const FormRow_ = styled('div')<{
  $isActive?: boolean;
  $isDragging?: boolean;
  $isHovered?: boolean;
  $isDraggable?: boolean;
  $gapAbove?: boolean;
  $suppressInlineGap?: boolean;
  $isComponent?: boolean;
}>(({ theme, $isActive, $isDragging, $isDraggable, $gapAbove, $isHovered, $suppressInlineGap, $isComponent }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  margin: '0px 16px',
  border: ($isComponent || $isActive)
    ? `1px solid ${theme.CreateFormTheme?.colors?.primary || '#0556CB'}`
    : ($isHovered
      ? `1px solid ${theme.CreateFormTheme?.colors?.primary || '#0556CB'}`
      : `1px solid ${theme.CreateFormTheme?.colors?.border || '#e0e0e0'}`),
  borderLeft: ($isComponent || $isActive)
    ? `4px solid ${theme.CreateFormTheme?.colors?.primary || '#0556CB'}`
    : ($isHovered
      ? `3px solid ${theme.CreateFormTheme?.colors?.primary || '#0556CB'}`
      : `1px solid ${theme.CreateFormTheme?.colors?.border || '#e0e0e0'}`),
  borderRadius: '4px',
  backgroundColor: $isComponent
    ? '#F6FAFF'
    : ($isHovered
      ? theme.CorporateTheme.colors.dropZoneBG
      : ($isDragging ? theme.CorporateTheme.colors.formBuilderDraggingBG : theme.CorporateTheme.colors.formBuilderNormalBG)),
  cursor: 'grab',
  transition: 'all 0.2s ease',
  // Stronger visual cue while hovered for dark theme
  boxShadow: $isHovered ? `inset 0 0 0 1px ${theme.CreateFormTheme?.colors?.primary || '#0556CB'}` : 'none',

  '::before': {
    content: "''",
    position: 'absolute',
    left: 0,
    right: 8,
    top: -1,
    height: 6,
    borderRadius: '4px',
    background: ($gapAbove && !$suppressInlineGap) ? `${theme.CreateFormTheme?.colors?.primary}33` : 'transparent',
    border: ($gapAbove && !$suppressInlineGap) ? `1px dashed ${theme.CreateFormTheme?.colors?.primary || '#0556CB'}` : '1px dashed transparent',
    transition: 'all 0.15s ease',
    pointerEvents: 'none',
  },

  '&:hover': {
    border: `1px solid ${theme.CreateFormTheme?.colors?.primary || '#0556cb'}`,
  },

  '&:active': {
    cursor: 'grabbing',
  },
  '& *': {
    cursor: 'inherit',
  },
}));

const DraggableIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: grab;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  &:active {
    cursor: grabbing;
  }
`;

const PageStepperContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '1rem',
  padding: '16px',
  background: theme.CorporateTheme.colors.background,
  borderRadius: '8px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  overflowX: 'auto',
  whiteSpace: 'nowrap',
}));

const PageStep = styled.button<{ $isActive: boolean }>`
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  border: none;
  /* Selected page: greyish background (theme headerBG), primary-colored text.
    Non-selected: primary background with white text. */
  background: ${({ $isActive }) => $isActive ? CreateFormTheme.colors.headerBG : CreateFormTheme.colors.primary};
  color: ${({ $isActive }) => $isActive ? CreateFormTheme.colors.primary : '#ffffff'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${({ $isActive }) => $isActive ? '#f5f7fa' : CreateFormTheme.colors.hoverPrimary};
  }
`;

const AddPageButton = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  border: none;
  background: ${CreateFormTheme.colors.primary};
  color: white;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: ${CreateFormTheme.colors.hoverPrimary};
  }
`;

// Tabs scroll + chevron buttons (theme-aware)
const TabsScrollWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  position: 'relative',
  marginBottom: '1rem',
}));

const TabsScrollInner = styled('div')(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  paddingBottom: 4,
}));

const ScrollButton = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: '38%',
  transform: 'translateY(-50%)',
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.CreateFormTheme?.colors?.headerBG || '#fff',
  border: `1px solid ${theme.CreateFormTheme?.colors?.border || '#e6e6e6'}`,
  boxShadow: '0 1px 6px rgba(0,0,0,0.12)',
  padding: 0,
  cursor: 'pointer',
  zIndex: 5,
  '&:hover': {
    transform: 'translateY(-50%) scale(1.03)'
  }
}));

const ScrollButtonLeft = styled(ScrollButton)`
  left: -15px;
`;

const ScrollButtonRight = styled(ScrollButton)`
  right: -30px;
`;

const FadeEdge = styled('div')<{ $visible?: boolean; $left?: boolean }>(({ theme, $visible, $left }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 44,
  pointerEvents: 'none',
  zIndex: 4,
  left: $left ? 0 : undefined,
  right: $left ? undefined : 0,
  opacity: $visible ? 1 : 0,
  transition: 'opacity 200ms ease',
  background: $left
    ? `linear-gradient(to right, ${theme.CreateFormTheme?.colors?.headerBG || '#fff'}, rgba(255,255,255,0))`
    : `linear-gradient(to left, ${theme.CreateFormTheme?.colors?.headerBG || '#fff'}, rgba(255,255,255,0))`,
}));

const MemoizedFieldChip = React.memo(FieldChip);

export default FormBuilder;